import type {
  CreateLiteLogRequestDto,
  LiteLogDataInfo,
  SaveLiteLogChunkDto,
  UpdateLiteLogInfoDto,
  SaveLiteLogChunkBatchDto,
  SaveLiteLogChunkBatchCompressedDto,
  LiteGasReading,
  LiteCalibrationLog,
  LiteGasAlarmLog,
  LiteEventLog,
  LiteGeneralConfigLog,
  LiteSensorConfigLog,
  LatestGasReadingInfo,
  ConfigurationLogGrouped
} from '@packages/types/device/lite/log-dto';

/*
 * Project: bellcloud
 * File: apps/frontend/src/api/liteLog.ts
 * Author: SWAN
 * Created: 2025-11-13
 * Description: LITE 로그 데이터 API
 */

import { apiHelpers, extractApiData } from '../../core/api.helpers';

export type {
  CreateLiteLogRequestDto,
  LiteLogDataInfo,
  SaveLiteLogChunkDto,
  UpdateLiteLogInfoDto,
  SaveLiteLogChunkBatchDto,
  SaveLiteLogChunkBatchCompressedDto,
  LiteGasReading,
  LiteCalibrationLog,
  LiteGasAlarmLog,
  LiteEventLog,
  LiteGeneralConfigLog,
  LiteSensorConfigLog,
  LatestGasReadingInfo,
  ConfigurationLogGrouped,
} from '@packages/types/device/lite/log-dto';


/**
 * 로그 요청 생성
 */
export const createLogRequest = async (dto: CreateLiteLogRequestDto): Promise<LiteLogDataInfo> => {
  // 로그 생성은 빠르므로 기본 타임아웃 사용 (10초)
  const response = await apiHelpers.post<LiteLogDataInfo>('liteLogs.createRequest', dto);
  return extractApiData(response);
};

/**
 * 로그 메타데이터 업데이트
 */
export const updateLogInfo = async (
  logId: string,
  dto: UpdateLiteLogInfoDto
): Promise<LiteLogDataInfo> => {
  // 메타데이터 업데이트는 빠르므로 기본 타임아웃 사용 (10초)
  const response = await apiHelpers.patch<LiteLogDataInfo>('liteLogs.updateInfo', dto, { logId });
  return extractApiData(response);
};

/**
 * 청크 저장
 */
export const saveLogChunk = async (dto: SaveLiteLogChunkDto): Promise<any> => {
  const response = await apiHelpers.post('liteLogs.saveChunk', dto);
  return extractApiData(response);
};

/**
 * 청크 배치 저장 (Burst 모드 최적화)
 */
export const saveLogChunkBatch = async (dto: SaveLiteLogChunkBatchDto): Promise<number> => {
  // 배치 저장은 시간이 오래 걸릴 수 있으므로 타임아웃을 120초로 설정
  // 대용량 로그(28,000+ 청크)의 경우 처리 시간이 길어질 수 있음
  const response = await apiHelpers.post<number>(
    'liteLogs.saveChunkBatch',
    dto,
    {},
    {
      timeout: 120000, // 120초 (2분)
    }
  );
  return extractApiData(response);
};

/**
 * 압축된 청크 배치 저장 (FW >= 5.0.0)
 */
export const saveLogChunkBatchCompressed = async (
  dto: SaveLiteLogChunkBatchCompressedDto
): Promise<number> => {
  const response = await apiHelpers.post<number>(
    'liteLogs.saveChunkBatchCompressed',
    dto,
    {},
    {
      timeout: 120000,
    }
  );
  return extractApiData(response);
};

/**
 * 로그 정보 조회
 */
export const getLogInfo = async (logId: string): Promise<LiteLogDataInfo> => {
  const response = await apiHelpers.get<LiteLogDataInfo>('liteLogs.getLogInfo', { logId });
  return extractApiData(response);
};

/**
 * 기기별 로그 목록 조회
 */
export const getLogsByDevice = async (
  serialNumber: string,
  limit?: number
): Promise<LiteLogDataInfo[]> => {
  const queryParams = limit ? { limit } : undefined;
  const response = await apiHelpers.get<LiteLogDataInfo[]>(
    'liteLogs.getLogsByDevice',
    { serialNumber },
    queryParams
  );
  return extractApiData(response);
};

/**
 * 누락된 청크 조회
 */
export const getMissingChunks = async (logId: string): Promise<number[]> => {
  const response = await apiHelpers.get<number[]>('liteLogs.getMissingChunks', { logId });
  return extractApiData(response);
};

/**
 * 로그 완료 처리
 */
export const completeLog = async (logId: string): Promise<LiteLogDataInfo> => {
  // 완료 처리는 파싱을 트리거하므로 타임아웃을 120초로 설정
  const response = await apiHelpers.post<LiteLogDataInfo>(
    'liteLogs.completeLog',
    undefined,
    {
      logId,
    },
    {
      timeout: 120000, // 120초
    }
  );
  return extractApiData(response);
};

/**
 * 로그 파싱 (다운로드 완료 후 호출)
 */
export const parseLog = async (logId: string): Promise<any> => {
  // 파싱은 시간이 오래 걸릴 수 있으므로 타임아웃을 120초로 설정
  const response = await apiHelpers.post<any>(
    'liteLogs.parseLog',
    undefined,
    { logId },
    {
      timeout: 120000,
    }
  );
  return extractApiData(response);
};

/**
 * 로그 실패 처리
 */
export const failLog = async (logId: string, errorMessage: string): Promise<LiteLogDataInfo> => {
  const response = await apiHelpers.post<LiteLogDataInfo>(
    'liteLogs.failLog',
    { error_message: errorMessage },
    { logId },
    {
      timeout: 60000,
    }
  );
  return extractApiData(response);
};

/**
 * Gas Reading 데이터 조회
 */
export const getGasReadings = async (
  logId: string,
  limit?: number,
  offset?: number,
  date?: string // YYYY-MM-DD 형식
): Promise<LiteGasReading[]> => {
  const queryParams: Record<string, any> = {};
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  if (date !== undefined) queryParams.date = date;
  const response = await apiHelpers.get<LiteGasReading[]>(
    'liteLogs.gasReadings',
    { logId },
    queryParams
  );
  return extractApiData(response);
};

/**
 * 기기별 사용 가능한 날짜 목록 조회 (실제 데이터의 timestamp 기준)
 */
export const getAvailableDatesByDevice = async (serialNumber: string): Promise<string[]> => {
  const queryParams = {
    serial_number: serialNumber,
  };
  const response = await apiHelpers.get<string[]>(
    'liteLogs.availableDatesByDevice',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * 기기별 Gas Reading 조회 (serial_number + date 기반)
 */
export const getGasReadingsByDevice = async (
  serialNumber: string,
  date: string, // YYYY-MM-DD 형식 (필수)
  limit?: number,
  offset?: number
): Promise<LiteGasReading[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    date: date,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<LiteGasReading[]>(
    'liteLogs.gasReadingsByDevice',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * Calibration Log 데이터 타입 (LITE - timestamp로 그룹핑)
 */
/**
 * 기기별 Calibration Log 조회 (serial_number + 기간)
 */
export const getCalibrationLogsByDevice = async (
  serialNumber: string,
  startDate: string, // YYYY-MM-DD 형식 (필수)
  endDate: string, // YYYY-MM-DD 형식 (필수)
  limit?: number,
  offset?: number
): Promise<LiteCalibrationLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<LiteCalibrationLog[]>(
    'liteLogs.calibrationLogs',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * Gas Alarm Log 데이터 타입 (LITE)
 */
/**onExcelExportReady
 * 기기별 Gas Alarm Log 조회 (serial_number + 기간)
 */
export const getGasAlarmLogsByDevice = async (
  serialNumber: string,
  startDate: string, // YYYY-MM-DD 형식 (필수)
  endDate: string, // YYYY-MM-DD 형식 (필수)
  limit?: number,
  offset?: number
): Promise<LiteGasAlarmLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<LiteGasAlarmLog[]>(
    'liteLogs.gasAlarmLogs',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * Event Log 데이터 타입 (LITE)
 */
/**
 * 기기별 Event Log 조회 (serial_number + 기간)
 */
export const getEventLogsByDevice = async (
  serialNumber: string,
  startDate: string, // YYYY-MM-DD 형식 (필수)
  endDate: string, // YYYY-MM-DD 형식 (필수)
  limit?: number,
  offset?: number
): Promise<LiteEventLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<LiteEventLog[]>('liteLogs.eventLogs', {}, queryParams);
  return extractApiData(response);
};

/**
 * General Config Log 데이터 타입 (LITE)
 */
/**
 * 기기별 General Config Log 조회
 */
export const getGeneralConfigLogsByDevice = async (
  serialNumber: string,
  startDate: string,
  endDate: string,
  limit?: number,
  offset?: number
): Promise<LiteGeneralConfigLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<LiteGeneralConfigLog[]>(
    'liteLogs.generalConfigLogs',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * Sensor Config Log 데이터 타입 (LITE)
 */
/**
 * 기기별 Sensor Config Log 조회
 */
export const getSensorConfigLogsByDevice = async (
  serialNumber: string,
  startDate: string,
  endDate: string,
  limit?: number,
  offset?: number
): Promise<LiteSensorConfigLog[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<LiteSensorConfigLog[]>(
    'liteLogs.sensorConfigLogs',
    {},
    queryParams
  );
  return extractApiData(response);
};

/**
 * 가스 리딩 최신 정보 조회 (다운로드 최적화용)
 */
export const getLatestGasReadingInfo = async (
  serialNumber: string
): Promise<LatestGasReadingInfo> => {
  const response = await apiHelpers.get<LatestGasReadingInfo>(
    'liteLogs.latestGasReadingInfo',
    {},
    { serial_number: serialNumber }
  );
  return extractApiData(response);
};

/**
 * Configuration Log Grouped 인터페이스 (timestamp로 그룹화)
 */
/**
 * 기기별 Configuration Log 조회 (timestamp로 그룹화)
 */
export const getConfigurationLogsByDevice = async (
  serialNumber: string,
  startDate: string,
  endDate: string,
  limit?: number,
  offset?: number
): Promise<ConfigurationLogGrouped[]> => {
  const queryParams: Record<string, any> = {
    serial_number: serialNumber,
    start_date: startDate,
    end_date: endDate,
  };
  if (limit !== undefined) queryParams.limit = limit;
  if (offset !== undefined) queryParams.offset = offset;
  const response = await apiHelpers.get<ConfigurationLogGrouped[]>(
    'liteLogs.configurationLogs',
    {},
    queryParams
  );
  return extractApiData(response);
};
